import { WellnessMetricDto } from '@repo/shared/dtos/analytics/WellnessDto';
import { palette, seriesColors } from '../shared/theme';

export type WellnessMetricKey = 'pleasant' | 'calm_excited' | 'tense' | 'energetic';

export const calculateCompletionRate = (completed: number, total: number): number =>
  total > 0 ? (completed / total) * 100 : 0;

export const parseScaleLabels = (scale: string) => {
  const parts = scale.split(' • ');
  return {
    low: parts[0]?.split(' = ')[1] || 'Low',
    mid: parts[1]?.split(' = ')[1] || 'Neutral',
    high: parts[2]?.split(' = ')[1] || 'High',
  };
};

export const getImprovementColor = (metricLabel: string, improvementPercentage: number): string => {
  const isReductionGood = metricLabel.includes('Stress') || metricLabel.includes('Tension');

  if (isReductionGood) {
    return improvementPercentage < 0 ? 'text-green-600' : 'text-red-600';
  }
  return improvementPercentage >= 0 ? 'text-green-600' : 'text-red-600';
};

export const findBestMetricImprovement = (metricsData: WellnessMetricDto) => {
  const improvements = Object.entries(WELLNESS_METRIC_CONFIG)
    .map(([key, config]) => {
      const metric = metricsData[key as keyof WellnessMetricDto];
      const raw = metric.improvement_percentage;
      if (raw === null) return null;
      // For stress/tension, a decrease is an improvement — invert so higher = better
      const effectiveImprovement = config.isReductionBetter ? -raw : raw;
      return {
        name: config.displayName,
        metricKey: key as keyof typeof WELLNESS_METRIC_CONFIG,
        effectiveImprovement,
        actual: raw,
      };
    })
    .filter(
      (item): item is NonNullable<typeof item> =>
        item !== null && item.effectiveImprovement > 0,
    );

  if (improvements.length === 0) {
    return { name: 'No Data', metricKey: null as any, magnitude: 0, actual: 0 };
  }

  return improvements.reduce((prev, current) =>
    current.effectiveImprovement > prev.effectiveImprovement ? current : prev,
  );
};

export interface WellnessMetricDisplay {
  id: string;
  metric: string;
  pre: number | null;
  post: number | null;
  improvement: number | null;
  improvementPercentage: number | null;
  [key: string]: string | number | null;
}

export const WELLNESS_METRIC_CONFIG = {
  pleasant: {
    label: 'Emotional\nReadiness',
    shortLabel: 'Emotional Readiness',
    displayName: 'Emotional',
    color: seriesColors[1],
    isReductionBetter: false,
  },
  tense: {
    label: 'Tension\nLevel',
    shortLabel: 'Tension Level',
    displayName: 'Tension',
    color: palette.red,
    isReductionBetter: true,
  },
  calm_excited: {
    label: 'Stress\nLevel',
    shortLabel: 'Stress Level',
    displayName: 'Stress',
    color: palette.red,
    isReductionBetter: true,
  },
  energetic: {
    label: 'Physical\nReadiness',
    shortLabel: 'Physical Readiness',
    displayName: 'Physical',
    color: seriesColors[0],
    isReductionBetter: false,
  },
} as const;

export function transformWellnessMetrics(data: WellnessMetricDto | null): WellnessMetricDisplay[] {
  if (!data) return [];

  return Object.entries(WELLNESS_METRIC_CONFIG).map(([key, config]) => {
    const metric = data[key as keyof WellnessMetricDto];
    return {
      id: key,
      metric: config.label,
      pre: metric.avg_pre !== null ? Number(metric.avg_pre.toFixed(3)) : null,
      post: metric.avg_post !== null ? Number(metric.avg_post.toFixed(3)) : null,
      improvement: metric.improvement,
      improvementPercentage: metric.improvement_percentage,
    };
  });
}

export function formatWellnessExportData(data: WellnessMetricDto | null) {
  if (!data) return [];

  return Object.entries(WELLNESS_METRIC_CONFIG).map(([key, config]) => {
    const metric = data[key as keyof WellnessMetricDto];
    return {
      metric: config.shortLabel,
      avgPre: metric.avg_pre,
      avgPost: metric.avg_post,
      improvement: metric.improvement,
      improvementPercentage:
        metric.improvement_percentage !== null
          ? `${metric.improvement_percentage.toFixed(1)}%`
          : 'N/A',
    };
  });
}

export const getMetricKey = (label: string | number): WellnessMetricKey | null => {
  const labelStr = String(label);
  return Object.entries(WELLNESS_METRIC_CONFIG).find(
    ([_, config]) =>
      config.label === labelStr ||
      config.shortLabel === labelStr ||
      config.label.replace(/\n/g, ' ') === labelStr,
  )?.[0] as WellnessMetricKey | null;
};

export const getImprovementDisplay = (percentage: number) => {
  const sign = percentage > 0 ? '+' : '';
  const arrow = percentage > 0 ? '↗' : percentage < 0 ? '↘' : '→';
  return { sign, arrow, value: percentage.toFixed(1) };
};
